import { openingMonitoring } from './monitoring';
import { emptyTags } from './tags';
import type { Sanctuary, World } from './types';
import { SITES, type SiteId } from './data/sites';
import { AQUARIUM_SIDE_KM, DEFAULT_START } from './constants';
import { epochDayFromCivil, type CivilDate } from './calendar';
import { STREAM, carriedStreamSeed } from './streams';
import { Rng } from './rng';
import { openingRegions, sanctuaryEnvironment } from './systems/environment';
import { openingClimate, openingHeatwave, openingSst, sanctuaryRecord } from './systems/climate';
import { openingHeatStress } from './systems/heatStress';
import { sanctuaryOpeningCoral } from './systems/coral';
import { openingRegionPlankton, sanctuaryOpeningPlankton } from './systems/plankton';
import { sanctuaryOpeningFish, siteHasFish } from './systems/fish';
import { openingManagement } from './management';
import { sanctuaryOpeningTurtles } from './systems/turtles';
import { sanctuaryOpeningWhales } from './systems/whales';
import { sanctuaryOpeningSeals } from './systems/seals';
import { sanctuaryOpeningSharks } from './systems/sharks';
import { sanctuaryOpeningSlope } from './systems/slope';
import { openingMigrants } from './systems/migrants';
import { openingOperations } from './operations';
import { openingCampaign } from './campaign';

export interface WorldOptions {
  seed: number;
  conservancyName?: string;
  /** Where the game starts (`SITES`); Maui Nui unless a scenario says otherwise. */
  opening?: SiteId;
  /** The real date of day 0; `DEFAULT_START` unless a scenario says otherwise. */
  startDate?: CivilDate;
}

/** The epoch day of `DEFAULT_START`, which saves from before the real calendar are given (M12). */
export const DEFAULT_START_EPOCH_DAY = epochDayFromCivil(DEFAULT_START.year, DEFAULT_START.month, DEFAULT_START.day);

export function createWorld(options: WorldOptions): World {
  const seed = options.seed >>> 0;
  let nextEntityId = 1;
  const siteId = options.opening ?? 'maui_nui';
  const start = SITES[siteId];
  const startEpochDay = options.startDate
    ? epochDayFromCivil(options.startDate.year, options.startDate.month, options.startDate.day)
    : DEFAULT_START_EPOCH_DAY;

  // The opening year's climate: the record's, or, for a world opening beyond it, drawn (M24).
  const climateRng = Rng.fromState(carriedStreamSeed(seed, STREAM.climate));
  const climate = openingClimate(startEpochDay, climateRng);
  const record = sanctuaryRecord({ siteId, regionId: start.regionId });
  const sst = openingSst(record, climate, startEpochDay);
  const plankton = openingRegionPlankton(startEpochDay);

  const opening: Sanctuary = {
    id: nextEntityId++,
    name: start.name,
    siteId,
    regionId: start.regionId,
    site: { ...start.site },
    aquarium: { widthKm: AQUARIUM_SIDE_KM, lengthKm: AQUARIUM_SIDE_KM },
    establishedDay: 0,
    env: sanctuaryEnvironment({ site: start.site, siteId, regionId: start.regionId, sst }, startEpochDay),
    sst,
    heatwave: openingHeatwave(record, startEpochDay),
    heatStress: openingHeatStress(record, startEpochDay),
    coral: sanctuaryOpeningCoral({ siteId, regionId: start.regionId }, startEpochDay),
    plankton: sanctuaryOpeningPlankton(siteId, startEpochDay),
    fish: sanctuaryOpeningFish(siteId, startEpochDay),
    turtles: sanctuaryOpeningTurtles(siteId, startEpochDay),
    whales: sanctuaryOpeningWhales(siteId, startEpochDay),
    seals: sanctuaryOpeningSeals(siteId, startEpochDay),
    slope: sanctuaryOpeningSlope(siteId, startEpochDay),
    sharks: sanctuaryOpeningSharks(siteId, startEpochDay),
    management: openingManagement(siteHasFish(siteId)),
  };

  const world: World = {
    seed,
    rngState: carriedStreamSeed(seed, STREAM.ecology),
    climateRngState: climateRng.seed,
    climate,
    day: 0,
    startEpochDay,
    conservancyName: options.conservancyName ?? 'Seven Seas Conservancy',
    regions: openingRegions(startEpochDay, climate).map((r) => ({ ...r, plankton: plankton[r.id] })),
    migrants: openingMigrants(startEpochDay),
    sanctuaries: [opening],
    log: [
      {
        day: 0,
        severity: 'info',
        title: `${start.name} established`,
        detail: `A stewardship agreement covers ${AQUARIUM_SIDE_KM} km by ${AQUARIUM_SIDE_KM} km `
          + `of water around ${start.site.placeName}. The rest of the ocean is not yet yours to manage.`,
      },
    ],
    gameOver: null,
    nextEntityId,
    tags: emptyTags(),
    monitoring: {},
    operations: openingOperations({ monitoring: {}, startEpochDay, day: 0 }),
    campaign: openingCampaign({ startEpochDay, day: 0 }),
  };
  // What the monitoring programs last counted before day 0: the opening world, with each program's error (M160).
  world.monitoring = openingMonitoring(world);
  // Its opening funds and crew, its tourism fees set on that monitoring (M238).
  world.operations = openingOperations(world);
  return world;
}
